/**
 * 서식자료실 (?page=forms) — 좌측 목록(캐리어 탭·카테고리 그룹) / 우측 PDF 미리보기.
 * 모든 서식은 서버에서 PDF 변환(LibreOffice·img2pdf) — /api/tools/forms/:id/preview.
 * hwp 등 미지원 형식은 다운로드 안내. UPS 특송 C/S 공식 배포 채널.
 */
import React, { useState, useEffect, useMemo } from "react";
import ApiPortalLayout from "./ApiPortalLayout";

const CARRIER_TABS = ["전체", "공통", "UPS", "FEDEX", "DHL"];
const S = {
  page: { color: "#1f2937", fontFamily: "'Inter','Malgun Gothic', '맑은 고딕', 'Pretendard',sans-serif", lineHeight: 1.65 },
  h1: { fontSize: 26, fontWeight: 800, marginBottom: 4 },
  p: { fontSize: 13.5, color: "#4b5563", margin: "4px 0" },
  input: { height: 38, padding: "0 12px", fontSize: 13.5, borderRadius: 8,
    border: "1px solid #d1d5db", outline: "none", background: "#fff",
    fontFamily: "inherit", boxSizing: "border-box", width: "100%" },
  cat: { fontSize: 12, fontWeight: 700, color: "#9ca3af", margin: "12px 0 4px",
    textTransform: "uppercase", letterSpacing: 0.4 },
  item: (on) => ({
    display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
    borderRadius: 8, cursor: "pointer", marginBottom: 2,
    background: on ? "#111827" : "transparent",
    color: on ? "#fff" : "#374151",
  }),
  ext: (on) => ({
    fontSize: 10.5, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
    background: on ? "rgba(255,255,255,0.18)" : "#f3f4f6",
    color: on ? "#fff" : "#6b7280", textTransform: "uppercase", flexShrink: 0 }),
  dl: { background: "#111827", color: "#fff", borderRadius: 8, padding: "9px 18px",
    fontSize: 13.5, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" },
};
const fmtSize = (n) => {
  if (!n) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
};

export default function FormsPage({ onNavigate, embedded = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [carrier, setCarrier] = useState("전체");
  const [sel, setSel] = useState(null); // 선택 항목
  const narrow = typeof window !== "undefined" && window.innerWidth < 900;

  useEffect(() => {
    fetch("/api/tools/forms")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then((d) => {
        setData(d);
        const first = d.categories?.[0]?.items?.[0];
        if (first) setSel(first);
      })
      .catch(() => setErr("서식 목록을 불러오지 못했습니다 — 잠시 후 다시 시도해 주세요."));
  }, []);

  const cats = useMemo(() => {
    const all = data?.categories || [];
    const t = q.trim();
    return all
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (x) =>
            (carrier === "전체" || (x.carrier || "공통") === carrier) &&
            (!t || (x.title || "").includes(t) || (x.orig_name || "").includes(t)),
        ),
      }))
      .filter((c) => c.items.length);
  }, [data, q, carrier]);

  const counts = useMemo(() => {
    const m = { 전체: 0, 공통: 0, UPS: 0, FEDEX: 0, DHL: 0 };
    for (const c of data?.categories || []) {
      for (const x of c.items) {
        m["전체"] += 1;
        m[x.carrier || "공통"] = (m[x.carrier || "공통"] || 0) + 1;
      }
    }
    return m;
  }, [data]);

  const pick = (x) => {
    setSel(x);
    if (narrow && x.has_preview) {
      window.open(`/api/tools/forms/${x.id}/preview`, "_blank");
    }
  };

  return (
    <ApiPortalLayout active="forms" onNavigate={onNavigate} embedded={embedded}>
      <div style={S.page}>
        <h1 style={S.h1}>서식자료실</h1>
        <p style={S.p}>
          수출입 통관 서식 공식 배포 채널 — (주)로그인네트웍은 <b>UPS 특송 통관 C/S 업무</b>를
          대행합니다. 서식 작성 문의:{" "}
          <a href="/?page=contact"
            onClick={(e) => { e.preventDefault(); onNavigate("contact"); }}>문의하기</a>
        </p>

        {/* 캐리어 탭 */}
        <div style={{ display: "flex", gap: 6, margin: "12px 0" }}>
          {CARRIER_TABS.map((c) => (
            <button key={c}
              onClick={() => setCarrier(c)}
              style={{
                border: carrier === c ? "1.5px solid #111827" : "1px solid #d1d5db",
                borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
                background: carrier === c ? "#111827" : "#fff",
                color: carrier === c ? "#fff" : "#374151",
              }}>
              {c} {counts[c] ? `(${counts[c]})` : ""}
            </button>
          ))}
        </div>

        {err && <div style={{ fontSize: 13, color: "#b91c1c" }}>{err}</div>}
        {!err && !data && <div style={{ fontSize: 13, color: "#6b7280" }}>불러오는 중…</div>}

        {data && (
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            {/* 좌: 목록 */}
            <div style={{ width: narrow ? "100%" : 300, flexShrink: 0 }}>
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="서식 검색" style={S.input} />
              <div style={{ maxHeight: 620, overflowY: "auto", marginTop: 6 }}>
                {cats.map((c) => (
                  <div key={c.category}>
                    <div style={S.cat}>{c.category}</div>
                    {c.items.map((x) => {
                      const on = sel && sel.id === x.id;
                      return (
                        <div key={x.id} style={S.item(on)} onClick={() => pick(x)}>
                          <span style={S.ext(on)}>{x.ext}</span>
                          <span style={{
                            fontSize: 13.5, fontWeight: on ? 700 : 500,
                            overflow: "hidden", textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                            {x.title}
                          </span>
                          {(x.carrier || "공통") !== "공통" && (
                            <span style={{ ...S.ext(on), marginLeft: "auto" }}>{x.carrier}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {cats.length === 0 && (
                  <div style={{ fontSize: 13, color: "#6b7280", marginTop: 10 }}>
                    검색 결과가 없습니다.
                  </div>
                )}
              </div>
            </div>

            {/* 우: 미리보기 (모바일에서는 새 탭으로 대체) */}
            {!narrow && (
              <div style={{ flex: 1, minWidth: 0 }}>
                {sel ? (
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 12,
                    overflow: "hidden", background: "#fff" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10,
                      padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{sel.title}</div>
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>
                          {sel.orig_name} {sel.size_bytes ? `· ${fmtSize(sel.size_bytes)}` : ""}
                          {sel.note ? ` · ${sel.note}` : ""}
                        </div>
                      </div>
                      <a style={{ ...S.dl, marginLeft: "auto" }}
                        href={`/api/tools/forms/${sel.id}/download`}>
                        원본 다운로드 ↓
                      </a>
                    </div>
                    {sel.has_preview ? (
                      <iframe
                        title="서식 미리보기"
                        src={`/api/tools/forms/${sel.id}/preview`}
                        style={{ width: "100%", height: 640, border: "none",
                          display: "block", background: "#525659" }}
                      />
                    ) : (
                      <div style={{ padding: 40, textAlign: "center",
                        fontSize: 13.5, color: "#6b7280" }}>
                        이 형식({sel.ext})은 미리보기를 지원하지 않습니다 —
                        원본을 다운로드해 확인하세요.
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ padding: 40, textAlign: "center",
                    fontSize: 13.5, color: "#6b7280" }}>
                    왼쪽 목록에서 서식을 선택하세요.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ background: "#f8fafc", borderLeft: "3px solid #94a3b8",
          padding: "10px 14px", fontSize: 12.5, color: "#475569",
          borderRadius: 4, marginTop: 16 }}>
          서식은 관세청·관계기관 고시 개정에 따라 변경될 수 있습니다 — 제출 전 최신 여부를
          확인하세요. 미리보기는 참고용 변환본이며 제출은 원본 파일을 사용하세요.
        </div>
      </div>
    </ApiPortalLayout>
  );
}
